import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { PriceAlertType } from 'src/app/models/price-alert';
import { PriceAlertService } from 'src/app/api/price-alert.service';
import { FormBuilder, Validators } from '@angular/forms';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics.service';
import { Options } from '@angular-slider/ngx-slider';
import { FiatExchangeRateService } from 'src/app/api/fiat-exchange-rate.service';
import { environment } from 'src/environments/environment';

enum PriceAlertMode {
  ThresholdValue = 'ThresholdValue',
  Percentage = 'Percentage'
}

@Component({
  selector: 'price-alert-form',
  templateUrl: './price-alert-form.component.html',
  styleUrls: ['./price-alert-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PriceAlertFormComponent implements OnInit {
  @Input() pair: string;
  @Input() price: number = 0;
  @Input() useGlobalCurrency = false;
  @Output() submit = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  INITIAL_PERCENTAGE = 0.05;
  PriceAlertMode = PriceAlertMode;

  mainCurrency = '';
  baseCurrency = '';
  globalCurrency = environment.currencies.globalCurrency;
  globalCurrencyRate = 0;

  loading = false;
  error: string = null;
  success = false;

  mode = PriceAlertMode.ThresholdValue;

  percentage = 5;
  percentageOptions: Options = {
    floor: 1,
    ceil: 15,
    showSelectionBar: true,
    hidePointerLabels: true,
    translate: (value: number) => {
      return `${value}%`;
    }
  }

  form = this.fb.group({
    active: [true],
    below: [0],
    above: [0],
    sms: [false],
    email: [true]
  });

  constructor(
    private fb: FormBuilder,
    private priceAlertService: PriceAlertService,
    private fiatExchangeRateService: FiatExchangeRateService,
    private ga: GoogleAnalyticsService,
    private changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit() {
    const currencies = this.pair ? this.pair.split('-') : ['', ''];
    this.mainCurrency = currencies[0];
    this.baseCurrency = currencies[1];

    if (this.useGlobalCurrency) {
      this.getGlobalCurrencyRate();
    }

    this.setPrice(this.price);
  }

  getGlobalCurrencyRate() {
    this.fiatExchangeRateService.getGlobalCurrencyRate(this.baseCurrency).subscribe(response => {
      this.globalCurrencyRate = response;
    }, err => {
      this.globalCurrencyRate = 0;
      this.changeDetector.detectChanges();
    })
  }

  setPrice(price: number) {
    this.below.setValidators(Validators.max(price - 0.000000001));
    this.above.setValidators(Validators.min(price + 0.000000001));
    this.below.setValue(this.round(price * (1 - this.INITIAL_PERCENTAGE)));
    this.above.setValue(this.round(price * (1 + this.INITIAL_PERCENTAGE)));
    this.changeDetector.detectChanges();
  }

  toggleType(type: PriceAlertType) {
    switch (type) {
      case PriceAlertType.SMS:
        this.sms.setValue(!this.sms.value);
        break;
      case PriceAlertType.EMAIL:
        this.email.setValue(!this.email.value);
        break;
    }

    this.active.setValue(true);
    this.changeDetector.detectChanges();
    return false;
  }

  onPercentageChange(val: number) {
    this.percentage = val;
    this.above.setValue(this.price * (1 + (val / 100)));
    this.below.setValue(this.price * (1 - (val / 100)));

    this.changeDetector.detectChanges();
  }

  onThresholdChange() {
    if(!Number.isFinite(this.above.value) && !Number.isFinite(this.below.value)) {
      this.form.setErrors({invalid: true});
      return;
    }

    this.form.setErrors(null);
    this.changeDetector.detectChanges();
  }

  onSubmit() {
    this.loading = true;
    this.error = null;
    this.success = false;

    const types = [];

    if (this.active.value) {
      if (this.sms.value) { types.push(PriceAlertType.SMS); }
      if (this.email.value) { types.push(PriceAlertType.EMAIL); }

      if (types.length === 0) {
        this.loading = false;
        return this.error = 'you must enable one at least one of the notification types';
      }
    }

    const belowAmount = this.useGlobalCurrency ? this.below.value / this.globalCurrencyRate : this.below.value;
    const aboveAmount = this.useGlobalCurrency ? this.above.value / this.globalCurrencyRate : this.above.value;

    this.priceAlertService.save(
      {amount: belowAmount, pair: this.pair},
      {amount: aboveAmount, pair: this.pair},
      types
    ).subscribe(response => {
      this.ga.sendEvent(
        'Price Alert',
        {
          action: 'Submit',
          label: `${this.active.value ? 'Activate' : 'Inactivate'}${this.sms.value ? ' SMS' : ''}${this.email.value ? ' Email' : ''}`
        }
      );

      this.loading = false;
      this.success = true;
      this.changeDetector.detectChanges();
    }, err => {
      this.loading = false;
      this.error = err.errors[0].message;
      this.changeDetector.detectChanges();
    });
  }

  onCancel() {
    this.close.emit();
  }

  onFinish() {
    this.submit.emit();
  }

  setMode(mode: PriceAlertMode) {
    this.mode = mode;
  }

  round(number: any) {
    return Math.round(number * 10000.0) / 10000.0;
  }

  get currency() {
    if (this.useGlobalCurrency) {
      return environment.currencies.globalCurrency;
    } else {
      return this.baseCurrency;
    }
  }

  get active() { return this.form.get('active'); }
  get below() { return this.form.get('below'); }
  get above() { return this.form.get('above'); }
  get sms() { return this.form.get('sms'); }
  get email() { return this.form.get('email'); }
}
